import type { HTMLAttributes, ReactElement } from 'react';
import { Children, cloneElement } from 'react';

import type { AccordionPanelProps } from './AccordionPanel';
import { AccordionLegacy } from './legacy';
import type { AccordionRowItem } from './legacy/types';
import { AccordionVariant, themes } from './themes';

export interface AccordionLegacyProps {
  /**
   * Any rows of information that is needed for the accordion
   *
   * @deprecated Use {@link AccordionPanel} components as children instead.
   */
  rows: AccordionRowItem[];
}

export interface AccordionBaseProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The variant of the accordion.
   *
   * @defaultValue 'Kale'
   */
  variant?: AccordionVariant;
  /** Only use {@link AccordionPanel} components as children. */
  children:
    | ReactElement<AccordionPanelProps>
    | ReactElement<AccordionPanelProps>[];
}

function isLegacyAccordion(
  props: AccordionProps
): props is AccordionLegacyProps {
  return 'rows' in props;
}

export type AccordionProps = AccordionBaseProps | AccordionLegacyProps;

/**
 * Render a vertically stacked set of interactive headings, representing a
 * section of content. The content is expanded when then heading is clicked.
 * Each accordion has at least one {@link AccordionPanel} as children.
 *
 * @example
 *
 * ```tsx
 * <Accordion>
 *   <AccordionPanel heading="Heading 1">Content 1</AccordionPanel>
 *   <AccordionPanel heading="Heading 2">Content 2</AccordionPanel>
 * </Accordion>;
 * ```
 *
 * @see https://corgi-x.tfd.engineering/components/accordion @see
 * https://www.w3.org/WAI/ARIA/apg/patterns/accordion/examples/accordion/
 */
export function Accordion(props: AccordionProps) {
  if (isLegacyAccordion(props)) {
    return <AccordionLegacy {...props} />;
  }
  const { variant = AccordionVariant.Default, children, ...divProps } = props;
  return (
    <div {...divProps}>
      {Children.map(children, child =>
        cloneElement(child, { theme: themes[variant], variant })
      )}
    </div>
  );
}
